import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { getCatalogStorageSession, isCatalogStorageSessionCurrent } from '../utils/catalogSessionStorage';
import { createGooglePlacesUiKitSession } from '../utils/googlePlacesUiKitBrowser';
import { isGoogleCheckoutSnapshot, newGoogleCheckoutSessionKey } from '../utils/googleCheckoutSelection';
import { googleCheckoutSearchQuery } from '../utils/googleCheckoutSearchQuery';
import { isGoogleCheckoutAvailable } from '../utils/googleCheckoutAvailability';

// Coordinates and permits stay in this checkout's memory. This component does
// not write browser storage or use a Google SDK before an explicit search.
export default function GoogleCheckoutFallback({ apiUrl, eligible, query, street = '', number = '', city, state, draftKey, contextKey,
  disabled = false, onSelected, onSearchActiveChange }) {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const [message, setMessage] = useState('');
  const container = useRef(null);
  const generation = useRef(0);
  const owner = useRef('');
  const running = useRef(false);
  const sessionKey = useRef('');
  const dispose = useRef(null);
  const abort = useRef(null);
  const callback = useRef(onSelected); callback.current = onSelected;
  const activity = useRef(onSearchActiveChange); activity.current = onSearchActiveChange;
  const activeSearch = useRef(false);
  const searchQuery = googleCheckoutSearchQuery({ query, street, number });
  const identity = JSON.stringify([contextKey, draftKey, searchQuery, city, state, disabled]);
  if (owner.current !== identity) { owner.current = identity; generation.current += 1; }

  useEffect(() => {
    const sequence = ++generation.current;
    const session = getCatalogStorageSession();
    let active = true;
    setAvailable(false); setBusy(false); setStarted(false); setMessage(''); running.current = false;
    dispose.current?.(); dispose.current = null; abort.current?.abort();
    if (eligible && !disabled && searchQuery.length >= 3 && searchQuery.length <= 120 && city && state) {
      apiFetch(`${apiUrl}/api/delivery-pricing/google-fallback/status`).then(async response => {
        const data = await response.json().catch(() => ({}));
        if (active && sequence === generation.current && isCatalogStorageSessionCurrent(session)) {
          setAvailable(response.ok && isGoogleCheckoutAvailable(data));
        }
      }).catch(() => {});
    }
    return () => {
      active = false; generation.current += 1; dispose.current?.(); abort.current?.abort();
      if (activeSearch.current) { activeSearch.current = false; activity.current?.(false); }
    };
  }, [apiUrl, identity, eligible, searchQuery, city, state, disabled]);

  const search = async () => {
    if (!eligible || !available || disabled || running.current || started) return;
    running.current = true; setBusy(true); setStarted(true); setMessage('');
    activeSearch.current = true; activity.current?.(true);
    const sequence = generation.current;
    const identityAtStart = owner.current;
    const session = getCatalogStorageSession();
    const controller = new AbortController(); abort.current = controller;
    const current = () => sequence === generation.current && identityAtStart === owner.current
      && !controller.signal.aborted && isCatalogStorageSessionCurrent(session);
    const post = async (path, body) => {
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/google-fallback/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!current()) throw new Error('O endereço ou a sessão mudou. Faça uma nova busca.');
      if (!response.ok) throw new Error(data.error || 'A busca complementar está indisponível agora. Você pode preencher o endereço.');
      return data;
    };
    try {
      if (!sessionKey.current) sessionKey.current = newGoogleCheckoutSessionKey();
      const prepared = await post('reserve', { q: searchQuery, city, state, draftKey,
        sessionKey: sessionKey.current, idempotencyKey: newGoogleCheckoutSessionKey() });
      if (!current() || !container.current || prepared.query !== searchQuery) throw new Error('O endereço mudou. Faça uma nova busca.');
      const widget = await createGooglePlacesUiKitSession({ prepared, container: container.current,
        signal: controller.signal, locationBias: prepared.locationBias, locationRestriction: prepared.locationRestriction,
        onError: error => { if (current()) { setMessage(error); setBusy(false); } },
        onSuggestion: async suggestion => {
          if (!current()) return;
          setBusy(true);
          try {
            const receipt = await post('select', { permit: prepared.sessionPermit, position: suggestion.position,
              sessionKey: sessionKey.current, draftKey });
            if (!isGoogleCheckoutSnapshot(receipt.deliveryLocation)
              || !Number.isFinite(Date.parse(receipt.expiresAt)) || Date.parse(receipt.expiresAt) <= Date.now()
              || ['lat', 'lng'].some(field => receipt.deliveryLocation.position[field].toFixed(7) !== suggestion.position[field].toFixed(7))) {
              throw new Error('Não foi possível verificar a localização selecionada. Faça uma nova busca.');
            }
            if (current()) {
              activeSearch.current = false; activity.current?.(false);
              callback.current?.(receipt, prepared.query);
            }
          } catch (error) { if (current()) setMessage(error.message); }
          finally { if (current()) setBusy(false); }
        },
      });
      if (!current()) widget.dispose(); else { dispose.current = widget.dispose; setBusy(false); }
    } catch (error) { if (current()) { setMessage(error.message); setBusy(false); activeSearch.current = false; activity.current?.(false); } }
  };

  return <Stack spacing={1} sx={{ display: available || started ? undefined : 'none' }}>
    {available && !started && <Button size="small" disabled={disabled || busy} onClick={search}>
      A opção correta não apareceu? Pesquisar no Google
    </Button>}
    {started && <Typography variant="body2">{busy ? 'Verificando a localização...' : 'Selecione abaixo o local em que deseja receber o pedido.'}</Typography>}
    <Box ref={container} sx={{ display: started ? 'block' : 'none', maxWidth: '100%', overflow: 'auto' }} />
    {started && <Button size="small" onClick={() => {
      generation.current += 1; abort.current?.abort(); dispose.current?.(); dispose.current = null;
      running.current = false; setStarted(false); setBusy(false); setMessage('');
      activeSearch.current = false; activity.current?.(false);
    }}>Fechar busca Google</Button>}
    {message && <Alert severity="info">{message}</Alert>}
  </Stack>;
}
